import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { cargasApi } from '../api/cargas'
import { Card, CardHeader } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { FleetMap, type MapMarker } from '../components/FleetMap'

function DataRow({ label, value }: { label: string; value: string | number | null | undefined }) {
  return (
    <div className="flex justify-between py-2 border-b border-slate-100 dark:border-slate-700/60 last:border-0">
      <span className="text-sm text-slate-500 dark:text-slate-400">{label}</span>
      <span className="text-sm font-medium text-slate-800 dark:text-slate-200">{value ?? '—'}</span>
    </div>
  )
}

export default function CargaDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [photoZoom, setPhotoZoom] = useState(false)

  const { data, isLoading, error } = useQuery({
    queryKey: ['carga', id],
    queryFn: () => cargasApi.getById(id!),
    enabled: !!id,
  })

  const forensicMutation = useMutation({
    mutationFn: () => cargasApi.requestForensic(id!),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['carga', id] })
    },
  })

  if (isLoading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded w-48" />
        <div className="h-64 bg-slate-200 dark:bg-slate-700 rounded-xl" />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="text-center py-20 text-slate-400">
        <p className="text-lg">No se encontró la carga</p>
        <button onClick={() => navigate('/cargas')} className="mt-4 text-primary hover:underline text-sm">
          Volver a cargas
        </button>
      </div>
    )
  }

  const { carga, forensicReport } = data

  const markers: MapMarker[] = [{
    id: carga.id,
    lat: Number(carga.gpsLat),
    lng: Number(carga.gpsLng),
    color: '#2B6CFF',
    popup: `${carga.vehiculo?.codigo ?? 'Vehículo'} — ${Number(carga.litros).toFixed(0)} L`,
  }]

  return (
    <div className="space-y-5 max-w-5xl">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/cargas')} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
          ← Volver
        </button>
        <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">Detalle de carga</h1>
        <Badge estado={carga.estadoValidacion} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Foto */}
        <Card padding={false} className="overflow-hidden">
          <div
            className="relative cursor-zoom-in"
            onClick={() => setPhotoZoom(true)}
            title="Click para ampliar"
          >
            <img
              src={carga.fotoUrl}
              alt="Foto de la carga de combustible"
              className="w-full h-72 object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).src = 'https://placehold.co/600x300?text=Sin+foto'
              }}
            />
            <div className="absolute bottom-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded-full">
              Click para ampliar
            </div>
          </div>
        </Card>

        {/* Datos clave */}
        <Card>
          <CardHeader title="Datos de la carga" />
          <DataRow label="Fecha / Hora" value={new Date(carga.creadoAt).toLocaleString('es-BO')} />
          <DataRow label="Vehículo" value={carga.vehiculo?.codigo} />
          <DataRow label="Tipo máquina" value={carga.vehiculo?.tipoMaquina?.nombre} />
          <DataRow label="Operador" value={carga.operador?.nombre} />
          <DataRow label="Tanque" value={carga.tanque?.nombre} />
          <DataRow label="Litros" value={`${Number(carga.litros).toFixed(1)} L`} />
          <DataRow label="Precio/L" value={`Bs. ${Number(carga.precioPorLitro).toFixed(4)}`} />
          <DataRow label="Monto total" value={`Bs. ${Number(carga.montoTotal).toFixed(2)}`} />
          <DataRow label="Horómetro anterior" value={carga.horometroAnterior != null ? `${Number(carga.horometroAnterior).toFixed(0)} h` : '—'} />
          <DataRow label="Horómetro actual" value={`${Number(carga.horometroActual).toFixed(0)} h`} />
          <DataRow label="Horas trabajadas" value={carga.horasTrabajadas != null ? `${Number(carga.horasTrabajadas).toFixed(1)} h` : '—'} />
          <DataRow label="Consumo L/h" value={carga.consumoLitrosHora != null ? `${Number(carga.consumoLitrosHora).toFixed(2)} L/h` : '—'} />
          <DataRow label="Precisión GPS" value={carga.gpsAccuracy != null ? `${Number(carga.gpsAccuracy).toFixed(0)} m` : '—'} />
          <DataRow label="Origen" value={carga.origenCarga} />
          {carga.observacion && <DataRow label="Observación" value={carga.observacion} />}
        </Card>
      </div>

      {/* Outlier flags */}
      {carga.outlierFlags && carga.outlierFlags.length > 0 && (
        <Card className="border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/20">
          <CardHeader title="Alertas detectadas" />
          <ul className="space-y-1">
            {carga.outlierFlags.map((flag) => (
              <li key={flag} className="flex items-center gap-2 text-sm text-orange-800 dark:text-orange-300">
                <span>⚠</span>
                <span>
                  {flag === 'consumo_alto' && 'Consumo por hora superior al umbral del tipo de máquina'}
                  {flag === 'gps_lejos_tanque' && 'Coordenadas GPS a más de 500 m del tanque registrado'}
                  {!['consumo_alto', 'gps_lejos_tanque'].includes(flag) && flag}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* Mini mapa */}
      <Card>
        <CardHeader title="Ubicación GPS" subtitle={`Lat ${Number(carga.gpsLat).toFixed(6)}, Lng ${Number(carga.gpsLng).toFixed(6)}`} />
        <FleetMap
          markers={markers}
          center={[Number(carga.gpsLng), Number(carga.gpsLat)]}
          zoom={15}
          height="300px"
        />
      </Card>

      {/* Forensic */}
      <Card>
        <CardHeader
          title="Análisis forense IA"
          subtitle={
            forensicReport
              ? forensicReport.creadoAt
                ? `Realizado el ${new Date(forensicReport.creadoAt).toLocaleString('es-BO')}`
                : 'Realizado'
              : 'No realizado aún'
          }
        />
        {forensicReport ? (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="text-3xl font-bold text-slate-900 dark:text-slate-100">
                {(forensicReport.probabilidadFraude * 100).toFixed(1)}%
              </div>
              <div>
                <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Probabilidad de fraude</p>
                <p className="text-xs text-slate-400">{forensicReport.modeloIA}</p>
              </div>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/60 rounded-lg p-3">
              {forensicReport.recomendacion}
            </p>
          </div>
        ) : (
          <div className="flex items-center gap-4">
            <p className="text-sm text-slate-500 dark:text-slate-400 flex-1">
              El análisis forense utiliza Claude Vision para evaluar la foto, cruzar los datos con el horómetro y la ubicación GPS, y estimar la probabilidad de irregularidades.
            </p>
            <Button
              onClick={() => forensicMutation.mutate()}
              loading={forensicMutation.isPending}
              variant="secondary"
              size="sm"
            >
              Solicitar análisis IA
            </Button>
          </div>
        )}
        {forensicMutation.isError && (
          <p className="text-xs text-danger mt-2">Error al solicitar análisis. Reintente.</p>
        )}
      </Card>

      {/* Photo zoom modal */}
      {photoZoom && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setPhotoZoom(false)}
          role="dialog"
          aria-label="Foto ampliada"
        >
          <img
            src={carga.fotoUrl}
            alt="Foto ampliada de la carga"
            className="max-h-[90vh] max-w-[90vw] rounded-xl object-contain"
          />
        </div>
      )}
    </div>
  )
}
